import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ChartBarIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  BellAlertIcon,
  ShieldCheckIcon,
  TagIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';

const navigation = [
  { name: 'Admin Dashboard', href: '/admin/dashboard', icon: ChartBarIcon },
  { name: 'Companies', href: '/app/superadmin/companies', icon: BuildingOfficeIcon, roles: ['SUPER_ADMIN'] },
  { name: 'User Management', href: '/app/admin/users', icon: UserGroupIcon },
  { name: 'Create User', href: '/app/admin/users/invite', icon: UserGroupIcon },
  { name: 'System Settings', href: '/admin/settings', icon: Cog6ToothIcon },
  { name: 'Reports', href: '/admin/reports', icon: DocumentChartBarIcon },
  { name: 'Inventory Tags', href: '/admin/tags', icon: TagIcon },
  { name: 'Notifications', href: '/admin/notifications', icon: BellAlertIcon },
  { name: 'Security', href: '/admin/security', icon: ShieldCheckIcon },
];

const AdminSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  // Helper function to determine if a nav item is active
  const isActive = (href) => {
    if (href === '/admin/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center justify-between w-full">
                    <div className="flex flex-col">
                      <Link to="/admin/dashboard">
                        <span className="sr-only">RocketScanner</span>
                        <img 
                          src="/assets/logos/RocketScanner_Horizontal_Dark.svg" 
                          alt="RocketScanner" 
                          className="h-12 w-auto" 
                        />
                      </Link>
                      <span className="text-xs font-bold text-blue-600">ADMIN</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="-mx-2 space-y-1">
                          {navigation.filter(item => !item.roles || (user?.role && item.roles.includes(user.role))).map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${isActive(item.href) ? 'bg-gray-50 text-primary-600' : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'}`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <item.icon
                                  className={`h-6 w-6 shrink-0 ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-primary-600'}`}
                                  aria-hidden="true"
                                />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                      
                      <li>
                        <div className="text-xs font-semibold leading-6 text-gray-400">RETURN TO</div>
                        <ul className="-mx-2 mt-2 space-y-1">
                          <li>
                            <Link
                              to="/app/dashboard"
                              className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                              onClick={() => setSidebarOpen(false)}
                            >
                              <ChartBarIcon className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-600" aria-hidden="true" />
                              User Dashboard
                            </Link>
                          </li>
                          {user?.role === 'SUPER_ADMIN' && (
                            <li>
                              <Link
                                to="/superadmin/dashboard"
                                className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                                onClick={() => setSidebarOpen(false)}
                              >
                                <Cog6ToothIcon className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-600" aria-hidden="true" />
                                Super Admin
                              </Link>
                            </li>
                          )}
                        </ul>
                      </li>
                      <li className="mt-auto">
                        <button
                          onClick={() => {
                            logout();
                            setSidebarOpen(false);
                          }}
                          className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400 group-hover:text-primary-600">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                          </svg>
                          Logout
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
          <div className="flex h-16 shrink-0 items-center">
            <div className="flex flex-col">
              <Link to="/admin/dashboard">
                <span className="sr-only">RocketScanner</span>
                <img 
                  src="/assets/logos/RocketScanner_Horizontal_Dark.svg" 
                  alt="RocketScanner" 
                  className="h-12 w-auto" 
                />
              </Link>
              <span className="text-xs font-bold text-blue-600">ADMIN PANEL</span>
            </div>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navigation.filter(item => !item.roles || (user?.role && item.roles.includes(user.role))).map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${isActive(item.href) ? 'bg-gray-50 text-primary-600' : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'}`}
                      >
                        <item.icon
                          className={`h-6 w-6 shrink-0 ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-primary-600'}`}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              
              <li>
                <div className="text-xs font-semibold leading-6 text-gray-400">RETURN TO</div>
                <ul className="-mx-2 mt-2 space-y-1">
                  <li>
                    <Link
                      to="/app/dashboard"
                      className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                    >
                      <ChartBarIcon className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-600" aria-hidden="true" />
                      User Dashboard
                    </Link>
                  </li>
                  {user?.role === 'SUPER_ADMIN' && (
                    <li>
                      <Link
                        to="/superadmin/dashboard"
                        className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                      >
                        <Cog6ToothIcon className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-600" aria-hidden="true" />
                        Super Admin
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
              <li className="mt-auto">
                <button
                  onClick={logout}
                  className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400 group-hover:text-primary-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                  </svg>
                  Logout
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
